/**
 * Exercise progress (step 6): pure helpers, no DOM, no network.
 * Canonical units after the adapter match records: kg / seconds / meters.
 */
import { getLocale, t, type MessageKey } from "../i18n";
import { formatDistanceTotal, formatSeconds, type ExerciseType } from "./activity";
import { formatRecordValue, type RecordKind } from "./records";

export type ProgressRange = "3m" | "1y" | "all";

export const PROGRESS_RANGES: readonly ProgressRange[] = ["3m", "1y", "all"] as const;
export const DEFAULT_PROGRESS_RANGE: ProgressRange = "3m";

export const RANGE_LABEL: Record<ProgressRange, MessageKey> = {
  "3m": "PERIOD_90D",
  "1y": "PROGRESS_RANGE_1Y",
  all: "PERIOD_ALL",
};

/** The backend sends 200 points at most; the chart never draws more. */
export const MAX_PROGRESS_POINTS = 200;

export interface ProgressPoint {
  /** ISO date (YYYY-MM-DD, a longer ISO timestamp is kept as sent). */
  date: string;
  /** kg | seconds | meters, by kind. */
  value: number;
}

export interface ProgressSeries {
  kind: RecordKind;
  points: ProgressPoint[];
}

export const progressKey = (exerciseId: string, range: ProgressRange) =>
  `progress:${exerciseId}:${range}`;

/** What a catalog exercise of this type charts when the response names no kind (none for sessions). */
export function defaultProgressKind(type: ExerciseType | null | undefined): RecordKind | null {
  switch (type) {
    case "strength":
      return "estOneRepMax";
    case "timed":
      return "maxDuration";
    case "distance":
      return "maxDistance";
    default:
      return null;
  }
}

/** Tooltip / readout value: "~92,5 кг", "2 мин 5 сек", "5000 м". */
export function formatProgressValue(kind: RecordKind, value: number): string {
  return formatRecordValue(kind, value);
}

const trim1 = (n: number) => String(Math.round(n * 10) / 10);

function clock(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** Compact "60–92 кг", "1:05–2:05", "850–1200 м" for the axis caption under the chart. */
export function formatProgressRange(kind: RecordKind, lo: number, hi: number): string {
  const same = Math.round(lo * 10) === Math.round(hi * 10);
  switch (kind) {
    case "maxWeight":
    case "estOneRepMax":
      return `${same ? trim1(hi) : `${trim1(lo)}–${trim1(hi)}`} ${t("KG")}`;
    case "maxDuration":
      return same ? clock(hi) : `${clock(lo)}–${clock(hi)}`;
    case "maxDistance":
      if (same) return formatDistanceTotal(hi);
      if (hi >= 1000) {
        const dec = (n: number) => {
          const s = trim1(n / 1000);
          return getLocale() === "en" ? s : s.replace(".", ",");
        };
        return `${dec(lo)}–${dec(hi)} ${t("UNIT_KM")}`;
      }
      return `${Math.round(lo)}–${Math.round(hi)} ${t("UNIT_METERS")}`;
  }
}
